import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { PasswordRules } from '../components/auth/PasswordRules';
import { isStrongPassword } from '../lib/passwordPolicy';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AuthShell, apiError } from '../components/auth/AuthShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [state, setState] = useState('checking');
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setState('invalid'); setError('Bağlantı geçersiz.'); return; }
    axios.get(`${API}/auth/reset-password/verify`, { params: { token } })
      .then(() => setState('ready'))
      .catch(err => { setState('invalid'); setError(apiError(err, 'Bağlantı geçersiz.')); });
  }, [token]);

  const valid = isStrongPassword(password) && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      toast.success('Şifreniz değiştirildi. Yeni şifrenizle giriş yapabilirsiniz.');
      navigate('/login');
    } catch (err) {
      toast.error(apiError(err, 'Şifre değiştirilemedi'));
    } finally { setSaving(false); }
  };

  return (
    <AuthShell title="Yeni Şifre Belirle" description={state === 'ready' ? 'Hesabınız için yeni bir şifre belirleyin.' : undefined} testId="reset-password-page">
      {state === 'checking' && <Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" />}
      {state === 'invalid' && (
        <div className="space-y-4">
          <p className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="reset-password-invalid">{error}</p>
          <Link to="/forgot-password" className="block text-center text-sm font-semibold underline text-slate-700" data-testid="reset-password-new-request">Yeni bağlantı iste</Link>
        </div>
      )}
      {state === 'ready' && (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">Yeni Şifre</Label>
            <Input id="new-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password-input" />
            <PasswordRules password={password} testId="reset-password-rules" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Yeni Şifre (Tekrar)</Label>
            <Input id="confirm-password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className={confirm && confirm !== password ? 'border-rose-500' : ''} data-testid="reset-password-confirm" />
            {confirm && confirm !== password && <p className="text-xs text-rose-600" data-testid="reset-password-mismatch">Şifreler eşleşmiyor</p>}
          </div>
          <Button type="submit" disabled={!valid || saving} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="reset-password-submit">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Şifreyi Değiştir
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
